import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useReportStore } from '../../store/reports/reportStore';
import ChatMessage from '../../components/chat/ChatMessage';
import useSchoolStore from '../../store/schoolStore';
import { getClassName } from '../../utils/schoolUtils';
import ClassAccordion from './components/ClassAccordion';
import { LuArrowLeft, LuFileText } from 'react-icons/lu';
import PageHeader from '../../components/common/PageHeader';
import Card from '../../components/common/Card';
import Badge, { toneForStatus } from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import ErrorWidget from '../../components/common/ErrorWidget';
import LoadingMessage from '../../components/common/LoadingMessage';

export default function ReportItems() {
    const { reportId } = useParams();
    const { schoolId } = useSchoolStore();
    const { reportItemsList, isLoadingReportItems, error, getReportItems } = useReportStore();
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        if (reportId) {
            getReportItems(reportId, schoolId);
            setSelectedItem(null);
        }
    }, [reportId, getReportItems]);

    const back = { to: `/dash/${schoolId}/reports/class`, label: 'Class reports' };

    if (isLoadingReportItems) {
        return <LoadingMessage message="Loading the report…" />;
    }

    if (error) {
        return (
            <div className="mx-auto max-w-6xl">
                <PageHeader title="Report" back={back} />
                <ErrorWidget error={{ message: error }} title="Could not load the report" onRetry={() => getReportItems(reportId, schoolId)} />
            </div>
        );
    }

    // Prepare grouped items regardless of view state so it stays available
    const groupedItems = reportItemsList.reduce((acc, item) => {
        const readableClass = item.className ? getClassName(schoolId, item.className) : 'Unassigned';
        if (!acc[readableClass]) acc[readableClass] = [];
        acc[readableClass].push(item);
        return acc;
    }, {});

    // Natural string sorting for class names
    const sortedClasses = Object.keys(groupedItems).sort((firstClass, secondClass) => {
        return firstClass.localeCompare(secondClass, undefined, { numeric: true, sensitivity: 'base' });
    });

    // One item open: its title, class and status, then the report itself.
    // The class list stays mounted (hidden) so opened classes stay open on the way back.
    const itemView = selectedItem && (
        <div className="mx-auto max-w-4xl">
            <div className="mb-1.5">
                <button
                    type="button"
                    onClick={() => setSelectedItem(null)}
                    className="inline-flex items-center gap-1.5 rounded text-[13px] font-medium text-ui-ink-2 transition-colors hover:text-ui-ink"
                >
                    <LuArrowLeft aria-hidden className="size-4" />
                    All items in this report
                </button>
            </div>
            <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-ui-ink">{selectedItem.title}</h1>
                    <p className="mt-1 text-sm text-ui-ink-2">
                        Class {selectedItem.className} {selectedItem.sec}
                    </p>
                </div>
                <Badge tone={toneForStatus(selectedItem.status)}>{selectedItem.status}</Badge>
            </div>
            <ChatMessage message={selectedItem.message} fullWidth />
        </div>
    );

    return (
        <>
            {itemView}
            <div className={`mx-auto max-w-6xl ${selectedItem ? 'hidden' : ''}`}>
                <PageHeader title="Report items" subtitle="Reports in this batch, grouped by class. Open a class to see its items." back={back} />

                {sortedClasses.length > 0 ? (
                    <div className="flex flex-col gap-3">
                        {sortedClasses.map(className => (
                            <ClassAccordion
                                key={className}
                                title={className}
                                items={groupedItems[className]}
                                onSelectItem={setSelectedItem}
                            />
                        ))}
                    </div>
                ) : (
                    <Card>
                        <EmptyState icon={<LuFileText />} title="No items in this report" message="This report has no student reports yet." />
                    </Card>
                )}
            </div>
        </>
    );
}
